import type { StoreCategory, StoreCategoryUpdate } from '@erp/shared';
import { AlertTriangle, ArrowLeft, CornerDownRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, confirmDialog, DataTable, type DataTableColumn, EmptyState, FormField, Input, RichTextEditor } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';

import { TranslationEditor } from './TranslationEditor.js';

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** Room for the `-2` de-dupe suffix inside StoreCategoryUpdateSchema's 160-char slug. */
const SLUG_BASE_MAX = 150;
/** The table's default 10 rows/page would routinely split a parent from its children. */
const PAGE_SIZE = 25;

/** The list endpoint adds this derived flag; optional so an older API still renders. */
type StoreCategoryRow = StoreCategory & { parentUnpublished?: boolean };

/** One row of the flattened publishing tree. */
interface TreeRow {
  cat: StoreCategoryRow;
  depth: number;
  /** "Parent / Child" — sorting on this keeps children under their parent. */
  path: string;
  parentName: string | null;
}

type Mode = { kind: 'list' } | { kind: 'edit'; category: StoreCategoryRow };

/**
 * Flatten the categories into parent-first tree order, mirroring the grouping in
 * features/categories/CategoriesPage.
 */
function flattenTree(cats: StoreCategoryRow[]): TreeRow[] {
  const nameById = new Map(cats.map((c) => [c.categoryId, c.name]));
  const byParent = new Map<string | null, StoreCategoryRow[]>();
  for (const c of cats) {
    // A parentId we cannot resolve (an inactive or foreign parent) is treated as
    // a root: otherwise the walk never reaches the row and it vanishes from the
    // page, which is precisely how a mis-parented category becomes unpublishable.
    const key = c.parentId && nameById.has(c.parentId) ? c.parentId : null;
    const list = byParent.get(key);
    if (list) list.push(c);
    else byParent.set(key, [c]);
  }
  for (const list of byParent.values()) list.sort((a, b) => a.name.localeCompare(b.name));

  const out: TreeRow[] = [];
  const seen = new Set<string>();
  const walk = (c: StoreCategoryRow, depth: number, prefix: string): void => {
    if (seen.has(c.categoryId)) return; // a parent cycle would otherwise loop forever
    seen.add(c.categoryId);
    const path = prefix ? `${prefix} / ${c.name}` : c.name;
    out.push({ cat: c, depth, path, parentName: c.parentId ? nameById.get(c.parentId) ?? null : null });
    for (const k of byParent.get(c.categoryId) ?? []) walk(k, depth + 1, path);
  };
  for (const r of byParent.get(null) ?? []) walk(r, 0, '');
  // A parent CYCLE (A→B→A) has no root, so the walk above never reaches either
  // row and both disappear from the one page a merchant can publish them from.
  // Emit whatever is left as a root — a broken hierarchy must still be visible
  // and fixable, not silently absent.
  for (const c of cats) if (!seen.has(c.categoryId)) walk(c, 0, '');
  return out;
}

/** The selected rows plus everything under them, still in parent-first order. */
function withDescendants(rows: TreeRow[], selected: TreeRow[]): TreeRow[] {
  const at = new Map(rows.map((r, i) => [r.cat.categoryId, i]));
  const wanted = new Set<string>();
  for (const s of selected) {
    const start = at.get(s.cat.categoryId);
    if (start === undefined) continue;
    wanted.add(s.cat.categoryId);
    // `rows` is in tree order, so a node's subtree is the run of deeper rows
    // immediately after it — no second parent→children map needed.
    for (let i = start + 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || row.depth <= s.depth) break;
      wanted.add(row.cat.categoryId);
    }
  }
  return rows.filter((r) => wanted.has(r.cat.categoryId));
}

/** Same slug shape as the tenant-slug suggestion on the platform create page. */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/['"]+/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_BASE_MAX)
    .replace(/-+$/, '');
}

/**
 * A slug for a bulk publish: the name, falling back to the code (an Arabic-only
 * name slugifies to nothing), de-duped with a numeric suffix because
 * store_categories is unique on (tenant_id, slug).
 */
function uniqueSlug(cat: StoreCategoryRow, used: Set<string>): string {
  const base = slugify(cat.name) || slugify(cat.code) || 'category';
  let candidate = base;
  for (let n = 2; used.has(candidate); n++) candidate = `${base}-${n}`;
  used.add(candidate);
  return candidate;
}

export function CmsStoreCategoriesPage(): JSX.Element {
  const { t } = useTranslation('cms');
  const canWrite = usePermission('cms.write');
  const [cats, setCats] = useState<StoreCategoryRow[] | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: 'list' });
  const [publishing, setPublishing] = useState(false);

  const load = (): void => {
    void api().get<{ items: StoreCategoryRow[] }>('/cms/store-categories').then((r) => setCats(r.data.items)).catch(() => setCats([]));
  };
  useEffect(load, []);

  const rows = useMemo(() => flattenTree(cats ?? []), [cats]);

  const columns: DataTableColumn<TreeRow>[] = useMemo(() => [
    {
      id: 'name', header: t('storeCategories.name'), sortValue: (r) => r.path,
      searchValue: (r) => `${r.path} ${r.cat.code} ${r.cat.slug ?? ''}`,
      cell: (r) => (
        <div className="flex items-center gap-1.5" style={{ paddingInlineStart: `${r.depth * 20}px` }}>
          {r.depth > 0 && <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-fg-subtle" aria-hidden />}
          <button type="button" className="text-start text-brand hover:underline" onClick={() => setMode({ kind: 'edit', category: r.cat })}>
            {r.cat.name}<span className="ms-2 font-mono text-xs text-fg-muted">{r.cat.code}</span>
          </button>
          {r.cat.parentUnpublished && (
            <Badge variant="warning" title={t('storeCategories.parentUnpublishedHint')}>
              <AlertTriangle className="h-3 w-3" aria-hidden />{t('storeCategories.parentUnpublished')}
            </Badge>
          )}
        </div>
      ),
    },
    {
      id: 'parent', header: t('storeCategories.parent'), sortValue: (r) => r.parentName ?? '',
      cell: (r) => r.parentName ? <span className="text-sm text-fg-muted">{r.parentName}</span> : <span className="text-fg-subtle">—</span>,
    },
    { id: 'slug', header: t('storeCategories.slug'), sortValue: (r) => r.cat.slug ?? '', cell: (r) => r.cat.slug ? <span className="font-mono text-xs">/{r.cat.slug}</span> : <span className="text-fg-subtle">—</span> },
    {
      id: 'published', header: t('storeCategories.published'), sortValue: (r) => (r.cat.isPublished ? 1 : 0),
      cell: (r) => <Badge variant={r.cat.isPublished ? 'success' : 'secondary'}>{r.cat.isPublished ? t('storeCategories.live') : t('settings.draft')}</Badge>,
    },
    { id: 'featured', header: t('storeCategories.featured'), align: 'end', sortValue: (r) => (r.cat.featured ? 1 : 0), cell: (r) => r.cat.featured ? '★' : '' },
  ], [t]);

  /**
   * Publish the selected nodes and every descendant. A child needs its own
   * published overlay row with a slug before the storefront tree can reach it,
   * and the merchant should not have to invent 40 slugs by hand — so a blank
   * slug is auto-generated here.
   */
  const publishSubtree = async (selected: TreeRow[], clear: () => void): Promise<void> => {
    const targets = withDescendants(rows, selected).filter((r) => !(r.cat.isPublished && r.cat.slug));
    if (targets.length === 0) { toast.info(t('storeCategories.alreadyPublished')); return; }
    if (!(await confirmDialog({ message: t('storeCategories.confirmPublishSubtree', { count: targets.length }) }))) return;

    setPublishing(true);
    // Seed the taken set with every slug we can see (published or not — the
    // unique index does not care).
    const used = new Set((cats ?? []).map((c) => c.slug).filter((s): s is string => Boolean(s)));
    let failed = 0;
    for (const r of targets) {
      let slug = r.cat.slug ?? uniqueSlug(r.cat, used);
      let done = false;
      for (let attempt = 0; attempt < 5 && !done; attempt++) {
        try {
          const payload: StoreCategoryUpdate = { isPublished: true, slug };
          await api().put(`/cms/store-categories/${r.cat.categoryId}`, payload);
          done = true;
        } catch (e) {
          // The unique index is the real arbiter: a 409 means something we could
          // not see holds the slug, so bump the suffix and try again.
          if ((e as { response?: { status?: number } }).response?.status !== 409) break;
          used.add(slug);
          slug = uniqueSlug(r.cat, used);
        }
      }
      if (!done) failed += 1;
    }
    setPublishing(false);
    clear();
    load();
    if (failed > 0) toast.error(t('storeCategories.publishSubtreeFailed', { count: failed }));
    else toast.success(t('storeCategories.publishedSubtree', { count: targets.length }));
  };

  if (mode.kind === 'edit') {
    return <CategoryForm category={mode.category} onDone={() => { setMode({ kind: 'list' }); load(); }} onCancel={() => setMode({ kind: 'list' })} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('storeCategories.heading')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('storeCategories.subtitle')}</p>
      </header>
      {!cats ? (
        <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>
      ) : cats.length === 0 ? (
        <EmptyState title={t('storeCategories.empty')} description={t('storeCategories.emptyHint')} />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={rows}
            rowKey={(r) => r.cat.categoryId}
            searchPlaceholder={t('storeCategories.search')}
            pageSize={PAGE_SIZE}
            selectable={canWrite}
            bulkActions={(selected, clear) => (
              <Button variant="ghost" size="sm" loading={publishing} onClick={() => void publishSubtree(selected, clear)}>
                {t('storeCategories.publishSubtree')}
              </Button>
            )}
          />
        </Card>
      )}
    </div>
  );
}

function CategoryForm({ category, onDone, onCancel }: { category: StoreCategory; onDone: () => void; onCancel: () => void }): JSX.Element {
  const { t } = useTranslation('cms');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('cms.write');
  const [isPublished, setIsPublished] = useState(category.isPublished);
  const [slug, setSlug] = useState(category.slug ?? '');
  const [featured, setFeatured] = useState(category.featured);
  const [sort, setSort] = useState(String(category.sort));
  const [descriptionHtml, setDescriptionHtml] = useState(category.descriptionHtml ?? '');
  const [seoTitle, setSeoTitle] = useState(category.seoTitle ?? '');
  const [seoDescription, setSeoDescription] = useState(category.seoDescription ?? '');
  const [saving, setSaving] = useState(false);

  const save = async (): Promise<void> => {
    if (slug.trim() && !SLUG.test(slug.trim())) { toast.error(t('storeCategories.badSlug')); return; }
    if (isPublished && !slug.trim()) { toast.error(t('storeCategories.slugRequired')); return; }
    setSaving(true);
    const payload: StoreCategoryUpdate = {
      isPublished,
      slug: slug.trim() || null,
      featured,
      sort: Number.isInteger(Number(sort)) ? Math.max(0, Number(sort)) : 0,
      descriptionHtml: descriptionHtml.trim() || null,
      seoTitle: seoTitle.trim() || null,
      seoDescription: seoDescription.trim() || null,
    };
    try {
      await api().put(`/cms/store-categories/${category.categoryId}`, payload);
      toast.success(t('storeCategories.saved'));
      onDone();
    } catch (e) {
      // The endpoint's only 409 is a duplicate slug; its only 422 is "slug required".
      const status = (e as { response?: { status?: number } }).response?.status;
      if (status === 409) toast.error(t('storeCategories.slugTaken'));
      else if (status === 422) toast.error(t('storeCategories.slugRequired'));
      else toast.error(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <button type="button" onClick={onCancel} className="inline-flex w-fit items-center gap-1 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-4 w-4" aria-hidden />{t('storeCategories.back')}
      </button>
      <h1 className="text-2xl font-semibold">{category.name} <span className="font-mono text-sm text-fg-muted">{category.code}</span></h1>

      <Card className="flex flex-col gap-4 p-5">
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isPublished} disabled={!canWrite} onChange={(e) => setIsPublished(e.target.checked)} className="h-4 w-4" />
            <span>{t('storeCategories.publish')}</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={featured} disabled={!canWrite} onChange={(e) => setFeatured(e.target.checked)} className="h-4 w-4" />
            <span>{t('storeCategories.featured')}</span>
          </label>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField id="cat-slug" label={t('storeCategories.slug')} helperText={t('storeCategories.slugHint')}>
            <Input id="cat-slug" value={slug} disabled={!canWrite} onChange={(e) => setSlug(e.target.value.toLowerCase())} maxLength={160} placeholder="electronics" />
          </FormField>
          <FormField id="cat-sort" label={t('storeCategories.sort')}>
            <Input id="cat-sort" type="number" min={0} value={sort} disabled={!canWrite} onChange={(e) => setSort(e.target.value)} />
          </FormField>
        </div>
        <FormField id="cat-desc" label={t('storeCategories.description')}>
          <RichTextEditor id="cat-desc" value={descriptionHtml} onChange={setDescriptionHtml} ariaLabel={t('storeCategories.description')} />
        </FormField>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField id="cat-seo-title" label={t('pages.seoTitle')}>
            <Input id="cat-seo-title" value={seoTitle} disabled={!canWrite} onChange={(e) => setSeoTitle(e.target.value)} maxLength={200} />
          </FormField>
          <FormField id="cat-seo-desc" label={t('pages.seoDescription')}>
            <Input id="cat-seo-desc" value={seoDescription} disabled={!canWrite} onChange={(e) => setSeoDescription(e.target.value)} maxLength={320} />
          </FormField>
        </div>
        {canWrite && <Button className="w-fit" onClick={() => void save()} loading={saving}>{tc('actions.save')}</Button>}
      </Card>

      <Card className="flex flex-col gap-3 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('pages.translate')}</h2>
        <TranslationEditor
          entityType="category"
          entityId={category.categoryId}
          disabled={!canWrite}
          fields={[
            { key: 'name', label: t('storeCategories.name') },
            { key: 'description', label: t('storeCategories.description'), type: 'richtext' },
          ]}
        />
      </Card>
    </div>
  );
}
